import * as React from "react";
import { createContext, useContext, useEffect } from "react";
import cn from "classnames";

import {
  StyledModalBody,
  StyledModalClose,
  StyledModalFooter,
  StyledModalFrame,
  StyledModalHeader,
  StyledModalShade,
  StyledModalTitle
} from "./style";
import {ModalFontSizes, ModalSizes} from "./types";

const ModalContext = createContext<{
  onClose: () => void
}>({
  onClose: () => null
});

function ModalClose() {
  const useModal = useContext(ModalContext);

  return <StyledModalClose className="ion-close" onClick={useModal.onClose} />;
}

interface IModalProps {
  visible: boolean;
  onClose: () => void;
  children: JSX.Element | JSX.Element[] | string;
  title?: string;
  size?: ModalSizes;
  fontSize?: ModalFontSizes;
}

export function Modal({ title, visible, onClose, children, size = "large", fontSize = "default" }: IModalProps): JSX.Element {
  const escFunction = (event: KeyboardEvent) => {
    if (event.keyCode === 27) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", escFunction);
    return function cleanup() {
      document.removeEventListener("keydown", escFunction);
    };
  }, []);

  return <>
    {visible ? (
      <ModalContext.Provider value={{ onClose }}>
        <StyledModalShade>
          <StyledModalFrame className={cn({
            [`width-${size}`]: size,
            [`font-size-${fontSize}`]: fontSize,
          })}>
            {
              title ? <StyledModalHeader>
                <StyledModalTitle>
                  {title}
                  <ModalClose />
                </StyledModalTitle>
              </StyledModalHeader> : null
            }
            {children}
          </StyledModalFrame>
        </StyledModalShade>
      </ModalContext.Provider>
    ) : null}
  </>;
}


interface IModalHeaderProps {
  children: React.ReactNode;
}

export function ModalHeader({ children }: IModalHeaderProps): JSX.Element {
  return <>
    <StyledModalHeader>
      <StyledModalTitle>
        {children}
        <ModalClose />
      </StyledModalTitle>
    </StyledModalHeader>
  </>;
}

interface IModalBodyProps {
  children: React.ReactNode;
}

export function ModalBody({ children }: IModalBodyProps): JSX.Element {
  return <>
    <StyledModalBody>
      <div>
        {children}
      </div>
    </StyledModalBody>
  </>;
}

interface IModalFooterProps {
  children?: React.ReactNode;
}

export function ModalFooter({ children }: IModalFooterProps): JSX.Element {
  return <>
    <StyledModalFooter>
      {children}
    </StyledModalFooter>
  </>;
}
